import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

import { SUPPORT_LEVEL_OPTIONS } from './TicketCustomerSidebar/utils';

const UnassignedLevelAlertBanner = ({ ticket, onTicketUpdated, isAssigned = true, onCreateTicketNote }) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [isDismissed, setIsDismissed] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const ticketId = ticket?.idTicket || ticket?.id;
    const hasNoLevel = Boolean(isAssigned && ticket && (!ticket.idCatNivelSoporte || Number(ticket.idCatNivelSoporte) === 0));

    // Resetear al cambiar de ticket
    useEffect(() => {
        setIsDismissed(false);
        setSelectedLevel('');
    }, [ticketId]);

    // Escuchar evento para reabrir la alerta si el asesor hace clic en el badge superior
    useEffect(() => {
        const handleReopen = () => {
            setIsDismissed(false);
        };
        window.addEventListener('akasia:support:reopen_unassigned_level_alert', handleReopen);
        return () => {
            window.removeEventListener('akasia:support:reopen_unassigned_level_alert', handleReopen);
        };
    }, []);

    // Notificar al Navbar superior y móvil el estado de la alerta
    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('akasia:support:unassigned_level_alert_state', {
                detail: {
                    isDismissed,
                    hasNoLevel
                }
            })
        );
    }, [isDismissed, hasNoLevel]);

    if (isDismissed || !hasNoLevel) return null;

    const handleDismiss = () => {
        setIsDismissed(true);
    };

    const handleRegisterInternalNote = async (text, color = '#dbeafe') => {
        try {
            if (typeof onCreateTicketNote === 'function') {
                await onCreateTicketNote({
                    note: text,
                    noteColor: color
                });
            } else {
                await fetch('/api/SupportEngine/CreateTicketNote', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + token
                    },
                    body: JSON.stringify({
                        ticketId: parseInt(ticketId, 10),
                        note: text,
                        noteColor: color
                    })
                });
            }
        } catch (err) {
            console.warn('Error al registrar nota interna:', err);
        }
    };

    const handleAssignLevel = async levelValue => {
        const newLevel = parseInt(levelValue, 10);
        if (!newLevel || !ticketId || isSaving) return;

        const levelObj = SUPPORT_LEVEL_OPTIONS.find(opt => opt.value === newLevel);
        const levelLabel = levelObj?.label || `Nivel ${newLevel}`;

        setIsSaving(true);
        try {
            const response = await fetch('/api/SupportEngine/UpdateTicketSupportLevel', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    idTicket: parseInt(ticketId, 10),
                    idCatNivelSoporte: newLevel
                })
            });

            if (response.ok) {
                showSuccessToast('Nivel asignado', `El ticket se asignó a ${levelLabel}`);

                await handleRegisterInternalNote(`${nombreCompleto || 'El asesor'} actualizó el nivel de soporte a: ${levelLabel}.`);

                if (onTicketUpdated) {
                    onTicketUpdated();
                }
            } else {
                const err = await response.json().catch(() => ({}));
                showErrorToast(err.message || 'Error al actualizar el nivel de soporte');
            }
        } catch (err) {
            showErrorToast('Error de red al actualizar el nivel');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex-shrink-0 px-2 px-sm-3 pt-2 pb-1">
            <div
                className="position-relative d-flex align-items-start gap-2 gap-sm-3 p-2 p-sm-3 rounded-3"
                style={{
                    backgroundColor: isDark ? 'rgba(99, 102, 241, 0.14)' : '#eef2ff',
                    border: isDark ? '1px solid rgba(99, 102, 241, 0.45)' : '1px solid #c7d2fe',
                    color: isDark ? '#c7d2fe' : '#3730a3'
                }}
            >
                {/* Icono parpadeante */}
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        color: '#6366f1',
                        marginTop: '2px',
                        animation: 'blinkIndigo 1s infinite alternate'
                    }}
                >
                    <style>{`
                        @keyframes blinkIndigo {
                            0% { opacity: 1; }
                            100% { opacity: 0.3; }
                        }
                    `}</style>
                    <FontAwesomeIcon icon={faLayerGroup} className="fs-5 fs-sm-3" />
                </div>

                {/* Contenido central: En escritorio se distribuye en horizontal */}
                <div className="flex-grow-1 d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-1.5 gap-md-3 pe-4 pe-md-5">
                    {/* Fila izquierda: Badge + Título + Mensaje conciso */}
                    <div className="d-flex flex-column justify-content-center">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span
                                className="badge text-white px-2 py-0.5 text-uppercase"
                                style={{
                                    backgroundColor: isDark ? '#4f46e5' : '#4f46e5',
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em'
                                }}
                            >
                                ATENCIÓN AGENTE
                            </span>
                            <span
                                className="fw-bold"
                                style={{
                                    fontSize: '0.82rem',
                                    letterSpacing: '0.02em',
                                    color: isDark ? '#a5b4fc' : '#3730a3'
                                }}
                            >
                                TICKET SIN NIVEL ASIGNADO
                            </span>
                        </div>

                        {/* Mensaje descriptivo */}
                        <p
                            className="mb-0 mt-0.5 d-none d-sm-block"
                            style={{
                                fontSize: '0.78rem',
                                lineHeight: 1.35,
                                color: isDark ? '#c7d2fe' : '#312e81'
                            }}
                        >
                            Este ticket aún no cuenta con un nivel de soporte asignado. Selecciona el nivel para continuar con la atención.
                        </p>
                    </div>

                    {/* Lado derecho: Selector directo de nivel */}
                    <div className="flex-shrink-0 d-flex align-items-center gap-2">
                        <select
                            value={selectedLevel}
                            onChange={e => {
                                const val = e.target.value;
                                setSelectedLevel(val);
                                if (val) {
                                    handleAssignLevel(val);
                                }
                            }}
                            disabled={isSaving}
                            className="form-select form-select-sm py-1 px-2.5 fw-semibold shadow-none"
                            style={{
                                width: 'auto',
                                minWidth: '175px',
                                maxWidth: '220px',
                                fontSize: '0.74rem',
                                backgroundColor: isDark ? '#1e1e38' : '#ffffff',
                                color: isDark ? '#f8fafc' : '#1e1b4b',
                                border: isDark ? '1px solid #4f46e5' : '1px solid #818cf8',
                                borderRadius: '6px'
                            }}
                        >
                            <option value="">Seleccionar nivel...</option>
                            {SUPPORT_LEVEL_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>

                        {isSaving && (
                            <span className="d-inline-flex align-items-center gap-1 text-primary" style={{ fontSize: '0.75rem' }}>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Guardando...</span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Botón de cierre (✕) */}
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="position-absolute top-50 translate-middle-y end-0 me-2 btn btn-link p-1 text-decoration-none shadow-none"
                    style={{
                        color: isDark ? '#c7d2fe' : '#4338ca',
                        fontSize: '0.80rem',
                        lineHeight: 1,
                        cursor: 'pointer'
                    }}
                    title="Ocultar alerta (se reflejará en la barra superior)"
                    aria-label="Cerrar alerta"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

UnassignedLevelAlertBanner.propTypes = {
    ticket: PropTypes.object,
    onTicketUpdated: PropTypes.func,
    isAssigned: PropTypes.bool,
    onCreateTicketNote: PropTypes.func
};

export default UnassignedLevelAlertBanner;
